/**
 * NoteProposalsStrip — TASK-930: a typed note is a note, the same as a dictated one.
 *
 * The gap this closes: a dictated sentence ran the skill fan-out and became a summary, tasks,
 * DNA proposals and life events. The SAME sentence typed into the Notes tab became text with
 * up to ten inline tasks and nothing else — no knowledge graph, no DNA, no life event. Which
 * capture device the advisor happened to use decided whether the firm learned anything.
 *
 * It runs `POST /api/voice-note/structure` — the one place the three capture paths hand text
 * to a model, and therefore the one place the L6 de-identification boundary sits (TASK-929).
 * A second fan-out here would be a fourth path around that boundary, so this calls the same
 * endpoint the voice widget calls rather than reimplementing it.
 *
 * It PROPOSES only. Accepting a DNA/life-event proposal goes through `approveFields`, the same
 * L1/L3 human-verify gate the voice widget uses — and the server's own cascade then fires:
 * accepting a `values.life_event[...]` field raises the mandatory follow-ups
 * (`propose_life_event_actions`), which is how "her father passed away" becomes a
 * source-of-wealth review without this component knowing anything about compliance.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Sparkles, ListPlus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { structureVoiceNote, type StructureResult, type VoiceNoteTask } from '../../../services/voiceNoteService';
import { approveFields } from '../../../services/knowledgeGraphService';
import { taskService } from '../../../services/taskService';
import { buildTaskRequest } from '../../../utils/buildTaskRequest';
import type { ProposalItem } from '../../kyc/workspace/ExtractionProposal';
import type { TaskPriority, TaskType } from '../../../types/tasks';

interface Props {
  clientId: string;
  clientName?: string;
  /** The note's text — what the fan-out reads. */
  content: string;
  /** The saved note, so accepted work points back at what proposed it. */
  noteId?: string;
  onDismiss: () => void;
  /** Accepted tasks land in the Do column; the tab refetches so its cards stay truthful. */
  onCommitted?: () => void;
}

/** A field path reads as a life event when the extractor says so — never guessed from words. */
const isLifeEvent = (p: ProposalItem) => p.field_path.startsWith('values.life_event[');

const labelFor = (p: ProposalItem): string => {
  if (p.label) return p.label;
  const leaf = p.field_path.split('.').pop() || p.field_path;
  return leaf.replace(/\[\d+\]/g, '').replace(/_/g, ' ');
};

const describe = (value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    // A life event's own words first: the extractor's quote is what the advisor recognises.
    const parts = [v.event_type, v.quote ?? v.text, v.date].filter(Boolean).map(String);
    if (parts.length) return parts.join(' — ');
  }
  return String(value);
};

const NORM_PRIORITY: Record<string, TaskPriority> = {
  low: 'low', medium: 'medium', high: 'high', critical: 'critical', urgent: 'critical',
};

export const NoteProposalsStrip: React.FC<Props> = ({
  clientId, clientName, content, noteId, onDismiss, onCommitted,
}) => {
  const [result, setResult] = useState<StructureResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [acceptedDna, setAcceptedDna] = useState<Map<string, ProposalItem>>(new Map());
  const [acceptedTasks, setAcceptedTasks] = useState<Set<number>>(new Set());

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Selections are keyed by ARRAY INDEX, and a re-run replaces the array. Saving a second
    // note while this strip is open swaps `content` without unmounting the component, so
    // without this reset "task 0 and 1 accepted" would carry over onto a different note's
    // tasks — accepting work the advisor never looked at.
    setAcceptedTasks(new Set());
    setAcceptedDna(new Map());
    setDismissed(new Set());
    try {
      setResult(await structureVoiceNote(clientId, content, clientName));
    } catch {
      // A failed fan-out is not "this note contained nothing" — say which it was, or the
      // advisor reads silence as a verdict about their note.
      setError('The note could not be read for proposals.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, content, clientName]);

  useEffect(() => { void run(); }, [run]);

  const commit = async () => {
    if (!result) return;
    setCommitting(true);
    try {
      const fields = [...acceptedDna.values()].map((p) => ({
        field_path: p.field_path,
        value: p.value,
        confidence: p.confidence,
        // The origin is the NOTE, not a dictation — provenance the DNA card renders verbatim.
        source: 'client_note',
      }));
      if (fields.length) await approveFields(clientId, fields);

      // Cleared as each one lands, so a failure part-way through leaves ONLY the unfinished
      // ones selected. `approveFields` and the life-event cascade are both idempotent;
      // `createTask` is not, so a blind retry of the whole batch duplicated every task that
      // had already been created before the failure.
      const tasks = result.tasks
        .map((t, i) => [i, t] as const)
        .filter(([i]) => acceptedTasks.has(i));
      for (const [index, t] of tasks) {
        await taskService.createTask({
          ...buildTaskRequest({
            title: t.title,
            description: t.description,
            client_id: clientId,
            priority: NORM_PRIORITY[(t.priority || '').toLowerCase()] ?? 'medium',
            source: 'note',
            // TASK-701's rule, one path along: the extractor's classification is forwarded
            // verbatim and never invented. With none, the shared default applies.
            task_type: (t.task_type as TaskType) ?? 'task_general',
          }),
          // Provenance: the note that proposed the work, so the task card can cite it.
          ...(noteId ? { note_id: noteId } : {}),
        });
        setAcceptedTasks((sel) => {
          const next = new Set(sel);
          next.delete(index);
          return next;
        });
      }

      toast.success(
        `Accepted ${fields.length} proposal(s) and ${tasks.length} task(s) from this note.`,
      );
      // Cleared only on the path where everything landed; a partial failure keeps the strip
      // open with the remainder still selected.
      onCommitted?.();
      onDismiss();
    } catch (e: any) {
      toast.error(e?.message || 'The proposals could not be accepted.');
    } finally {
      setCommitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Reading this note…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        <span>{error}</span>
        <div className="flex gap-3">
          <button type="button" onClick={() => void run()} className="font-medium underline">Try again</button>
          <button type="button" onClick={onDismiss} className="font-medium underline">Dismiss</button>
        </div>
      </div>
    );
  }

  const proposals = (result?.dna_proposals ?? []).filter((p) => !dismissed.has(p.field_path));
  const tasks = result?.tasks ?? [];
  if (proposals.length === 0 && tasks.length === 0) return null;

  const selected = acceptedDna.size + acceptedTasks.size;

  return (
    <div className="mb-4 rounded-lg border border-kielis-navy/20 bg-kielis-gold/5 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-kielis-navy" />
        <h4 className="text-sm font-semibold text-gray-900">Proposals from this note</h4>
        <span className="text-xs text-gray-500">
          Nothing is saved until you accept it.
        </span>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto text-xs text-gray-500 underline hover:text-gray-800"
        >
          Dismiss all
        </button>
      </div>

      {proposals.length > 0 && (
        <ul className="mb-3 space-y-2">
          {proposals.map((p) => {
            const accepted = acceptedDna.has(p.field_path);
            return (
              <li
                key={p.field_path}
                className="flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{labelFor(p)}</span>
                    {isLifeEvent(p) && (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-800">
                        Life event
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-gray-600">{describe(p.value)}</p>
                  {isLifeEvent(p) && (
                    <p className="mt-1 text-xs text-gray-500">
                      Accepting this raises the reviews the firm owes on it.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    aria-label={accepted ? `Undo accept ${labelFor(p)}` : `Accept ${labelFor(p)}`}
                    aria-pressed={accepted}
                    onClick={() => setAcceptedDna((m) => {
                      const next = new Map(m);
                      if (next.has(p.field_path)) next.delete(p.field_path);
                      else next.set(p.field_path, p);
                      return next;
                    })}
                    className={`rounded-md p-1.5 ${accepted ? 'bg-green-600 text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-green-700'}`}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Dismiss ${labelFor(p)}`}
                    onClick={() => setDismissed((s) => new Set(s).add(p.field_path))}
                    className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {tasks.length > 0 && (
        <div className="mb-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase text-gray-500">
            <ListPlus className="h-3.5 w-3.5" />
            Follow-ups
          </div>
          <ul className="space-y-1.5">
            {tasks.map((t: VoiceNoteTask, i) => (
              <li key={`${t.title}-${i}`} className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
                <input
                  type="checkbox"
                  id={`note-task-${i}`}
                  checked={acceptedTasks.has(i)}
                  onChange={() => setAcceptedTasks((s) => {
                    const next = new Set(s);
                    next.has(i) ? next.delete(i) : next.add(i);
                    return next;
                  })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor={`note-task-${i}`} className="min-w-0 flex-1 text-sm text-gray-800">
                  {t.title}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => void commit()}
        disabled={committing || selected === 0}
        className="rounded-lg bg-kielis-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
      >
        {committing ? 'Saving…' : selected === 0 ? 'Select what to accept' : `Accept ${selected} item(s)`}
      </button>
    </div>
  );
};

export default NoteProposalsStrip;
